/*
 * File: SocialEngagement.tsx
 * Project: a-a
 * File Created: Tuesday, 17th June 2025 9:43:15 pm
 * Author: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Last Modified: Thursday, 18th June 2026 10:36:04 am
 * Modified By: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Copyright 2020 - 2025 Mutual Mobile, Mutual Mobile
 */

import { Box, Typography } from '@mui/material';
import SocialAccounts from './SocialAccounts/SocialAccounts';
import EngagedAudience from './EngagedAudience/EngagedAudience';
import MonthlyBarChart from './MonthlyBarChart/MonthlyBarChart';
import CustomButton from '../../../shared/CustomButton/CustomButton';
import zeplinColor from '../../../../theme/Color';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ClientService } from '../../../../service/client.service';
import CustomCard from '../../../shared/CustomCard/CustomCard';
import FollowerChart from './FollowerChart/FollowerChart';
import RoutePaths from '../../../../routes/RoutePaths';
import styles from './SocialEngagementTab.module.css';
import CustomAutocomplete from '../../../shared/CustomAutocomplete/CustomAutocomplete';
import { ReactComponent as Captiv8Icon } from '../../../../assets/captiv8-link-icon.svg';
import CustomAlert from '../../../shared/CustomAlert/CustomAlert';
import CustomAlertContain from '../../../shared/CustomAlert/CustomAlertContain';
import { LightTooltip } from '../../../shared/CustomTooltip/CustomTooltip';
import ChangeClientModal from './ChangeClientModal';
import CustomLoader from '../../../shared/CustomLoader/CustomLoader';
import { HuddleClientService } from '../../../../service/huddle-client.service';
import { LeadsService } from '../../../../service/leads.service';

const SocialEngagementTab = ({
  captive8Id,
  captive8Url,
  getDetails,
}: {
  captive8Id: string;
  captive8Url: string;
  getDetails: () => void;
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const client = location?.state?.client;
  const params = useParams();
  const [captiv8, setCaptiv8] = useState([]);
  const [socialEngagement, setSocialEngagement] = useState<any>(null);
  const [changeClient, setChangeClient] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [connectLoading, setConnectLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState('');
  const [unlinkLoading, setUnlinkLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState<{
    captiv8: any[];
  }>({
    captiv8: [],
  });

  const wait = useRef<any>();
  const [error, setError] = useState<string | null>(null);
  const getCaptive8Info = async (id: string) => {
    try {
      setLoading(true);
      await LeadsService.getAnalysis({ captiv8Id: id });
      const res = await ClientService.getCaptive8Info(id);
      console.log({ res });
      setSocialEngagement(res);
      setError(null);
    } catch (err) {
      if (!captive8Id) {
        setError('Client not found in Captive8');
      } else {
        setError('Something went wrong');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSelection = (item: any, type: string | boolean) => {
    const key = type as keyof typeof selectedItems;
    if (Array.isArray(item)) {
      setSelectedItems((prev) => ({ ...prev, [key]: [...item] }));
    } else if (item?.target?.value) {
      const {
        target: { value },
      } = item;

      setSelectedItems((prev) => ({
        ...prev,
        [key]: typeof value === 'string' ? value.split(',') : value,
      }));
    } else {
      setSelectedItems((prev) => ({
        ...prev,
        [key]: [item],
      }));
    }
  };
  const handleLink = async () => {
    try {
      setConnectLoading(true);
      const res = location?.pathname.includes('huddle')
        ? await HuddleClientService.editHuddleClient(
            {
              Captive8_Creator_Id__c: selectedItems?.captiv8[0]?.Id,
            },
            client?.AccountId || params?.id || '',
          )
        : await ClientService.editClient(
            {
              Captive8_Creator_Id__c: selectedItems?.captiv8[0]?.Id,
            },
            client?.AccountId || params?.id || '',
          );

      setSelectedItems({ captiv8: [] });
      getDetails();
      // getCaptive8Info(client?.AccountId);
      // getDetails(client?.AccountId || params?.id || '');
      setAlert('Captiv8 linked successfully');
    } catch (err) {
      console.log(err);
    } finally {
      setConnectLoading(false);
    }
  };
  const handleUnLink = async () => {
    try {
      setChangeClient(false);
      setUnlinkLoading(true);
      const res = location?.pathname.includes('huddle')
        ? await HuddleClientService.editHuddleClient(
            {
              Captive8_Creator_Id__c: '',
            },
            client?.AccountId || params?.id || '',
          )
        : await ClientService.editClient(
            {
              Captive8_Creator_Id__c: '',
            },
            client?.AccountId || params?.id || '',
          );

      setSelectedItems({ captiv8: [] });
      getDetails();
      // getDetails(client?.AccountId || params?.id || '');
      setAlert('Captiv8 unlinked successfully');
    } catch (err) {
      setAlert('Something went wrong');
    } finally {
      setUnlinkLoading(false);
    }
  };
  const suggestionCaptiv8API = async (value: string) => {
    try {
      setSearchLoading(true);
      const res = await ClientService.searchCaptiv8(value?.toLocaleLowerCase());

      setCaptiv8(
        res?.users?.map((val: any) => ({
          Id: val?.captiv8Id,
          Name: val?.name,
          avatar: val?.avatar,
          followers: val?.followers,
        })),
      );
    } catch (err) {
      setAlert('Something went wrong');
    } finally {
      setConnectLoading(false);
    }
  };

  const handleCaptiv8Search = async (e: React.SyntheticEvent, value: string) => {
    clearTimeout(wait.current);

    if (value) {
      const getSuggestion = setTimeout(() => suggestionCaptiv8API(value), 1000);
      wait.current = getSuggestion;
    }
  };

  useEffect(() => {
    // getDetails(params?.id || '');
    captive8Id && getCaptive8Info(captive8Id);
    !captive8Id && setError('Client not found in Captive8');
    sessionStorage.removeItem('dealSelectedFromOverview');
  }, [location?.pathname, captive8Id]);
  return error && !loading ? (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        // alignItems: 'center',
        height: { xs: 'calc(100vh - 340px)', xl: 'calc(100vh - 480px)' }, // Match the height of your content area
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          // alignItems: 'center',
          width: '400px',
          margin: 'auto',
          gap: { xs: '16px', xl: '24px' },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '12px', xl: '16px' } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '6px', xl: '8px' } }}>
            <Captiv8Icon className={styles.captiv8Icon} />
            <Typography variant='responsiveHeading3'>Connect a Client from Captiv8</Typography>
            <Typography variant='responsiveBody3' color={zeplinColor.Neutral9}>
              Use the search bar to find your client in Captiv8 and connect them to access insights
              directly in Huddle.
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            width: '100%',
          }}
        >
          <CustomAutocomplete
            loading={connectLoading}
            chip={true}
            searchIcon='/assets/Search.svg'
            required={false}
            options={captiv8 || []}
            getOptionLabel={(option: any) => (option?.Name ? option?.Name : '')}
            label={'Search client'}
            handleDeleteItems={() => {
              handleSelection([], 'captiv8');
            }}
            selectedItems={[]}
            handleSelection={(value) => {
              handleSelection(value, 'captiv8');
            }}
            value={selectedItems?.captiv8[0]}
            handleFilter={handleCaptiv8Search}
            placeholder='Search client'
            currentLength={captiv8?.length}
            totalLength={captiv8?.length}
            next={() => {}}
            multiple={false}
            inputBorder={50}
          />
        </Box>
        <CustomButton
          disabled={connectLoading || !selectedItems?.captiv8?.length ? true : false}
          onClick={() => {
            handleLink();
          }}
          variant='contained'
          color={'primary'}
          style={{
            width: '100%',

            borderRadius: '14px',
            height: 48,
            padding: '14px 16px',
            '&.Mui-focusVisible': { backgroundColor: '#333CAA' },
          }}
          textStyle={{ color: zeplinColor.Neutral1, fontSize: { xs: '14px', xl: '16px' } }}
          text={connectLoading ? 'Connecting...' : 'Connect'}
        />
      </Box>
    </Box>
  ) : (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gridTemplateRows: 'auto auto',
        gap: { xs: '12px', xl: '24px' },
        overflowY: 'auto',

        height: { xs: 'calc(100vh - 310px)', xl: 'calc(100vh - 360px)' }, // Adjust height based on your layout
      }}
    >
      <Box sx={{ position: 'absolute', top: -10, right: 0, display: 'flex', gap: '8px' }}>
        <Link
          to={captive8Url || 'https://captiv8.io/insights/signin'}
          style={{
            color: zeplinColor.BasicBlack,
            textDecoration: 'none',
          }}
          target='_blank'
          rel='noopener noreferrer'
        >
          <CustomCard
            styles={{
              borderRadius: '50px',
              padding: '10px 10px 10px 16px',
              backgroundColor: zeplinColor.Neutral1,
              border: `1px solid ${zeplinColor.Neutral6}`,
              display: 'flex',
              gap: '8px',
              height: { xs: 30, xl: 48 },
              // flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
            }}
          >
            <Typography variant='responsiveBody3'>Captive8</Typography>
            <img src='/assets/link-2.svg' width={20} height={20} />
          </CustomCard>
        </Link>
        <LightTooltip
          title={
            <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
              Change Client
            </Typography>
          }
          placement='bottom-end'
          style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
          // sx={{ [`& .${tooltipClasses.tooltip}`]: { backgroundColor: zeplinColor.Neutral1 } }}
        >
          <Box sx={{ width: '100%' }}>
            <CustomButton
              disabled={false}
              onClick={() => {
                setChangeClient(true);
              }}
              variant='contained'
              color={'primary'}
              buttonVariant='responsiveButtonLarge'
              style={{
                width: '100%',
                borderRadius: { xs: '10px', xl: '14px' },
                height: { xs: 30, xl: 48 },
                padding: { xs: '10px 12px', xl: '14px 16px' },
                '&.Mui-focusVisible': { backgroundColor: '#333CAA' },
              }}
              textStyle={{ color: zeplinColor.Neutral1 }}
              text={'Change Client'}

              // tabindex={4}
            />
          </Box>
        </LightTooltip>
      </Box>
      <SocialAccounts loading={loading} data={socialEngagement?.socialAccounts} />
      {/* <EngagedAudience social={socialEngagement?.engagedAudienceQuality} /> */}
      <FollowerChart data={socialEngagement?.followers} loading={loading} />
      <MonthlyBarChart
        title={'Monthly Engagement'}
        total={socialEngagement?.monthlyEngagement?.formattedTotal}
        percent={socialEngagement?.monthlyEngagement?.changePercent}
        data={socialEngagement?.monthlyEngagement}
        chartKey='engagement'
        loading={loading}
      />
      <MonthlyBarChart
        title={'Monthly Average Views'}
        total={socialEngagement?.monthlyViews?.formattedTotal}
        percent={socialEngagement?.monthlyViews?.changePercent}
        data={socialEngagement?.monthlyViews}
        chartKey='avgViews'
        loading={loading}
      />
      <CustomAlert
        open={alert ? true : false}
        handleClose={() => {
          setAlert('');
        }}
        message={
          <CustomAlertContain
            alertIcon={
              !alert?.includes('successfully') ? (
                <img src='/assets/error.svg' width={24} height={24} />
              ) : (
                <img src='/assets/checkmark-green.png' width={24} height={24} />
              )
            }
            alert={!alert?.includes('successfully') ? 'Something went wrong' : alert}
          />
        }
        error={!alert?.includes('successfully') ? true : false}
      />
      <ChangeClientModal
        modalOpen={changeClient}
        setChangeClient={setChangeClient}
        handleUnLink={handleUnLink}
      />
      <CustomLoader open={unlinkLoading} />
    </Box>
  );
};

export default SocialEngagementTab;
